import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Chip } from "@mui/material";
import TestStoreBadge from "../../components/TestStoreBadge";

const formatAddress = (store) => {
  if (!store?.address) return "N/A";
  const { street, city, postcode, state, country } = store.address;
  const parts = [street, city, postcode, state, country].filter(Boolean);
  return parts.length ? parts.join(", ") : "N/A";
};

const getStoreUrl = (store) => {
  const currentPortal = sessionStorage.getItem("selectedPortal");
  if (currentPortal === "Merchant") {
    return `/merchantStore/${store._id}`;
  }
  return `/merchant/${store.merchantAccount}/store/${store._id}`;
};

const StoreList = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const navigate = useNavigate();

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...data].sort((a, b) => {
    if (sortConfig.key === null) return 0;
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    if (sortConfig.key === "address") {
      aVal = formatAddress(a);
      bVal = formatAddress(b);
    }
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "fit-content",
        maxWidth: "calc(100vw - 200px)",
        margin: "0 auto",
        overflowX: "auto",
        "&::-webkit-scrollbar": {
          height: "8px"
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "4px"
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "4px",
          "&:hover": {
            background: "#555"
          }
        }
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          borderCollapse: "separate",
          "td, th": { borderBottom: "1px solid rgba(224, 224, 224, 1)" }
        }}
        aria-label="merchant stores table"
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
            <TableCell
              sx={{ fontWeight: "bold", cursor: "pointer" }}
              onClick={() => requestSort("name")}
            >
              Store Name
              {sortConfig.key === "name" && (
                <span>{sortConfig.direction === "asc" ? " ▲" : " ▼"}</span>
              )}
            </TableCell>
            <TableCell
              sx={{ fontWeight: "bold", cursor: "pointer" }}
              onClick={() => requestSort("storeManager")}
            >
              Manager
              {sortConfig.key === "storeManager" && (
                <span>{sortConfig.direction === "asc" ? " ▲" : " ▼"}</span>
              )}
            </TableCell>
            <TableCell
              sx={{ fontWeight: "bold", cursor: "pointer" }}
              onClick={() => requestSort("address")}
            >
              Address
              {sortConfig.key === "address" && (
                <span>{sortConfig.direction === "asc" ? " ▲" : " ▼"}</span>
              )}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((store, index) => {
            const isDeleted = store.active === false;
            return (
            <TableRow
              key={store._id}
              onClick={() => navigate(getStoreUrl(store))}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: isDeleted ? "rgba(211, 47, 47, 0.08)" : "rgba(0, 0, 0, 0.04)"
                }
              }}
            >
              <TableCell
                component="th"
                scope="row"
                sx={isDeleted ? { color: "error.main", fontWeight: 600 } : undefined}
              >
                {index + 1}
              </TableCell>
              <TableCell sx={isDeleted ? { color: "error.main", fontWeight: 600 } : undefined}>
                <span className="inline-flex items-center gap-2 flex-wrap">
                  <span>{store.name || "N/A"}</span>
                  <TestStoreBadge store={store} />
                </span>
              </TableCell>
              <TableCell sx={isDeleted ? { color: "error.main" } : undefined}>
                {store.storeManager || "N/A"}
              </TableCell>
              <TableCell sx={isDeleted ? { color: "error.main" } : undefined}>
                {formatAddress(store)}
              </TableCell>
              <TableCell>
                <Chip
                  label={store.active ? "Active" : "Deleted"}
                  color={store.active ? "success" : "error"}
                  size="small"
                />
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StoreList;
